/**
 * Scannen „Mehrfach“: eine Sammelliste und eine Aktion für alle (ADR 0013 §5).
 *
 * - Jeder Scan kommt in die Liste, mit „×“ zum Entfernen. Ein
 *   **Packstück-QR** wird eingesammelt -- die Kiste ist ein Artikel.
 * - Ein **Lagerort-QR** (fester Ort) wählt das Ziel vor. Bestätigt wird mit
 *   einem großen Knopf; daneben lässt sich der Ort als Stammplatz festlegen.
 * - Die Aktionen stehen direkt unter der Kamera (Rückmeldungen 4.2):
 *   **Verschieben** groß, **Status** und **Wieder da** klein daneben. Alle
 *   gehen offline und durch die Warteschlange; nach der Aktion ist die Liste
 *   leer, und „Rückgängig“ nimmt alles zurück.
 * - Beim Verschieben setzt der Haken „Als neuen Stammplatz setzen“ den
 *   Stammplatz mit (4.3, braucht Netz). „Wieder da“ bucht an den Stammplatz,
 *   erst nach einer Übersicht; was dort abgewählt ist, bleibt in der Liste
 *   (4.1). Ändert sich ein Stammplatz, erinnert ein Hinweis ans Etikett (4.4).
 *
 * Kein Dialog mitten im Scannen: Was nicht passt, steht in der Rückmeldung,
 * und ein unbekannter Code bekommt seinen Ausweg (CLAUDE.md §7, KONZEPT.md §8.3).
 */

import { useCallback, useState } from "react";
import type { Ziel } from "@thw/core-domain";
import { useOnline } from "../api/online.js";
import { bestandLaden, useBestand, type Bestand } from "../lokal/bestand.js";
import { stammplatzSetzen } from "../lokal/stammplatz.js";
import {
  neuerVorgang,
  schonDort,
  statusSetzen,
  verboten,
  verschieben,
  woWar,
  zielName,
  type Gebucht,
} from "../lokal/verschieben.js";
import { aufloesenIn, codeAufloesen } from "../scan/aufloesen.js";
import { Scanner, rueckmeldungGeben } from "../scan/Scanner.jsx";
import { Knopf, Leer, Nummer, StatusPunkt } from "../ui/bausteine.jsx";
import { useBuchungsmeldung } from "../ui/Buchungsmeldung.jsx";
import { EtikettHinweis } from "../ui/EtikettHinweis.jsx";
import { GeraetAnlegen } from "../ui/GeraetAnlegen.jsx";
import { OhneCode } from "../ui/OhneCode.jsx";
import { StatusWahl } from "../ui/StatusWahl.jsx";
import {
  Nachzuerfassen,
  UnbekannterCodeText,
  unbekanntErfassen,
  unbekanntKopf,
  type Unbekannt,
} from "../ui/UnbekannterCode.jsx";
import { WiederDa } from "../ui/WiederDa.jsx";
import { Wohin } from "../ui/Wohin.jsx";

/** Taugt das Ziel als Stammplatz? Nicht an einer Einsatzstelle, nicht „Ohne festen Platz“. */
function taugtAlsStammplatz(bestand: Bestand, ziel: Ziel): boolean {
  if (ziel.art !== "lagerort") return false;
  const l = bestand.lagerorte.get(ziel.id);
  return l !== undefined && l.aktiv && !l.standardziel;
}

/** „2 schon dort, 1 geht nicht“ -- was ausgelassen wurde. */
function ausgelassenText(schon: number, geht: number, schonWort: string): string | undefined {
  const teile: string[] = [];
  if (schon > 0) teile.push(`${String(schon)} ${schonWort}`);
  if (geht > 0) teile.push(`${String(geht)} geht nicht (Packstück in sich selbst)`);
  return teile.length === 0 ? undefined : `Ausgelassen: ${teile.join(", ")}.`;
}

export function Mehrfach({
  liste,
  setListe,
}: {
  liste: readonly string[];
  setListe: (neu: (bisher: readonly string[]) => readonly string[]) => void;
}) {
  const online = useOnline();
  const bestand = useBestand();
  const [ziel, setZiel] = useState<Ziel | null>(null);
  const [aktion, setAktion] = useState<"aus" | "wohin" | "status" | "wiederDa">("aus");
  const [anlegen, setAnlegen] = useState<Unbekannt | null>(null);
  const { setMeldung, anzeige } = useBuchungsmeldung();

  const hinzufuegen = useCallback(
    (b: Bestand, artikel_id: string) => {
      const artikel = b.artikel.get(artikel_id);
      if (artikel === undefined) return;
      const schon = liste.includes(artikel_id);
      rueckmeldungGeben(true);
      setMeldung({
        art: schon ? "still" : "gut",
        kopf: schon ? `${artikel.bezeichnung} ist schon in der Liste` : `+ ${artikel.bezeichnung}`,
        zusatz: `${artikel.inventarnummer} · ${String(liste.length + (schon ? 0 : 1))} in der Liste`,
      });
      setListe((bisher) => (bisher.includes(artikel_id) ? bisher : [...bisher, artikel_id]));
    },
    [liste, setListe, setMeldung],
  );

  const aufCode = useCallback(
    async (code: string) => {
      let b = await bestandLaden();
      let gefunden = aufloesenIn(b, code);
      if (gefunden.art === "unbekannt") {
        gefunden = await codeAufloesen(code);
        b = await bestandLaden();
      }

      if (gefunden.art === "lagerort") {
        const l = gefunden.lagerort;
        // Ein Packstück-QR meint die Kiste als Ding -- sie kommt in die Liste.
        if (l.artikel_id !== null && b.artikel.has(l.artikel_id)) {
          hinzufuegen(b, l.artikel_id);
          return;
        }
        // Ein fester Ort wählt das Ziel vor.
        const neu: Ziel = { art: "lagerort", id: l.id };
        setZiel(neu);
        rueckmeldungGeben(true);
        setMeldung({
          art: "gut",
          kopf: `Ziel: ${zielName(b, neu)}`,
          zusatz: "Unten bestätigen — oder weiter scannen.",
        });
        return;
      }
      if (gefunden.art === "artikel") {
        hinzufuegen(b, gefunden.artikel.id);
        return;
      }

      rueckmeldungGeben(false);
      // Der Ausweg (ADR 0026): mit Netz anlegen, ohne Netz ist er gemerkt.
      const u = await unbekanntErfassen(
        gefunden.code,
        gefunden.vorschlag,
        ziel?.art === "lagerort" ? ziel.id : null,
      );
      setMeldung({
        art: "warnung",
        kopf: unbekanntKopf(u),
        zusatz: (
          <UnbekannterCodeText
            unbekannt={u}
            aufAnlegen={() => {
              setMeldung(null);
              setAnlegen(u);
            }}
            aufVergessen={() => {
              setMeldung(null);
            }}
          />
        ),
      });
    },
    [hinzufuegen, setMeldung, ziel],
  );

  /** Nach dem Anlegen steht das neue Gerät gleich in der Liste. */
  const angelegt = (artikel_id: string): void => {
    setAnlegen(null);
    void bestandLaden().then((b) => {
      hinzufuegen(b, artikel_id);
    });
  };

  /** Alle in der Liste an ein Ziel -- ein Vorgang, „Rückgängig“ für alle. */
  const alleVerschieben = async (wohin: Ziel, alsStammplatz = false): Promise<void> => {
    try {
      const b = await bestandLaden();
      const vorgang = neuerVorgang();
      const gebucht: Gebucht[] = [];
      const passend: string[] = [];
      let schon = 0;
      let geht = 0;
      for (const id of liste) {
        if (verboten(b, id, wohin)) {
          geht += 1;
          continue;
        }
        passend.push(id);
        if (schonDort(b.zustaende.get(id), wohin)) {
          schon += 1;
        } else {
          gebucht.push(await verschieben(b, id, wohin, vorgang));
          vorgang.stelle += 1;
        }
      }
      // Der Haken „Als neuen Stammplatz setzen“ (Rückmeldungen 4.3): ein
      // Stammdatum, nur mit Netz. Scheitert er, ist trotzdem verschoben.
      let stammplatzFehler: string | null = null;
      let neuerStammplatz: string[] = [];
      if (alsStammplatz && wohin.art === "lagerort" && passend.length > 0) {
        try {
          await stammplatzSetzen(passend, wohin.id);
          neuerStammplatz = passend;
        } catch (fehler) {
          stammplatzFehler = fehler instanceof Error ? fehler.message : String(fehler);
        }
      }
      const ausgelassen = ausgelassenText(schon, geht, "schon dort");
      rueckmeldungGeben(true);
      setMeldung({
        art: geht > 0 || stammplatzFehler !== null ? "warnung" : "gut",
        kopf: `✓ ${String(gebucht.length)} nach ${zielName(b, wohin)} verschoben`,
        zusatz: (
          <>
            {ausgelassen}
            {stammplatzFehler !== null && ` Stammplatz nicht gesetzt: ${stammplatzFehler}`}
            <EtikettHinweis bestand={b} artikel_ids={neuerStammplatz} />
          </>
        ),
        ...(gebucht.length > 0 ? { gebucht } : {}),
      });
      setListe(() => []);
      setZiel(null);
    } catch (fehler) {
      rueckmeldungGeben(false);
      setMeldung({
        art: "warnung",
        kopf: "Nicht gebucht",
        zusatz: fehler instanceof Error ? fehler.message : String(fehler),
      });
    }
  };

  const alleStatus = async (schluessel: string, bezeichnung: string): Promise<void> => {
    try {
      const b = await bestandLaden();
      const vorgang = neuerVorgang();
      const gebucht: Gebucht[] = [];
      let schon = 0;
      for (const id of liste) {
        if (b.zustaende.get(id)?.status === schluessel) {
          schon += 1;
          continue;
        }
        gebucht.push(await statusSetzen(b, id, schluessel, vorgang));
        vorgang.stelle += 1;
      }
      rueckmeldungGeben(true);
      setMeldung({
        art: "gut",
        kopf: `✓ Status ${bezeichnung} für ${String(gebucht.length)}`,
        zusatz: ausgelassenText(schon, 0, "hatten ihn schon"),
        ...(gebucht.length > 0 ? { gebucht } : {}),
      });
      setListe(() => []);
    } catch (fehler) {
      rueckmeldungGeben(false);
      setMeldung({
        art: "warnung",
        kopf: "Nicht gebucht",
        zusatz: fehler instanceof Error ? fehler.message : String(fehler),
      });
    }
    setAktion("aus");
  };

  /** Der Stammplatz für alle -- ein Stammdatum, nur mit Netz. Die Liste bleibt. */
  const alleStammplatz = async (lagerort_id: string): Promise<void> => {
    try {
      await stammplatzSetzen(liste, lagerort_id);
      const b = await bestandLaden();
      rueckmeldungGeben(true);
      setMeldung({
        art: "gut",
        kopf: `Stammplatz ${zielName(b, { art: "lagerort", id: lagerort_id })} für ${String(liste.length)}`,
        zusatz: (
          <>
            Die Liste bleibt — etwa um sie gleich dorthin zu verschieben.
            <EtikettHinweis bestand={b} artikel_ids={liste} />
          </>
        ),
      });
    } catch (fehler) {
      rueckmeldungGeben(false);
      setMeldung({
        art: "warnung",
        kopf: "Stammplatz nicht gesetzt",
        zusatz: fehler instanceof Error ? fehler.message : String(fehler),
      });
    }
  };

  if (anlegen !== null) {
    return (
      <GeraetAnlegen
        code={anlegen.code}
        vorschlag={anlegen.vorschlag}
        ort_id={anlegen.ort_id}
        aufAbbruch={() => {
          setAnlegen(null);
        }}
        aufVorhanden={(artikel) => {
          angelegt(artikel.id);
        }}
        aufFertig={(artikel) => {
          angelegt(artikel.id);
        }}
      />
    );
  }
  if (aktion === "wohin") {
    return (
      <Wohin
        titel={`Wohin? — ${String(liste.length)} verschieben`}
        stammplatzAnbieten
        aufAbbruch={() => {
          setAktion("aus");
        }}
        aufWahl={(z, alsStammplatz) => {
          setAktion("aus");
          void alleVerschieben(z, alsStammplatz);
        }}
      />
    );
  }
  if (aktion === "wiederDa") {
    return (
      <WiederDa
        ids={liste}
        aufAbbruch={() => {
          setAktion("aus");
        }}
        aufGebucht={(gebucht) => {
          setAktion("aus");
          const weg = new Set(gebucht.map((g) => g.artikel.id));
          const rest = liste.filter((id) => !weg.has(id)).length;
          setListe((bisher) => bisher.filter((id) => !weg.has(id)));
          rueckmeldungGeben(true);
          setMeldung({
            art: "gut",
            kopf: `✓ ${String(gebucht.length)} zurück an den Stammplatz`,
            zusatz: rest === 0 ? undefined : `${String(rest)} bleiben in der Liste.`,
            ...(gebucht.length > 0 ? { gebucht } : {}),
          });
        }}
      />
    );
  }

  const anzahl = liste.length;
  const zielStammplatz =
    bestand !== undefined && ziel !== null && taugtAlsStammplatz(bestand, ziel);

  return (
    <div className="flex flex-col gap-4">
      {anzeige}

      <Nachzuerfassen aufAnlegen={setAnlegen} />

      {/* Das vorgewählte Ziel -- per Lagerort-QR. Bestätigt wird mit dem großen Knopf. */}
      {ziel !== null && bestand !== undefined && (
        <section
          aria-label="Ziel"
          className="flex flex-col gap-2 rounded-xl border-2 border-blue-600 bg-blue-50 p-3 text-blue-950 dark:bg-blue-950 dark:text-blue-50"
        >
          <div className="flex items-center justify-between gap-2">
            <span>
              Ziel: <span className="font-bold">{zielName(bestand, ziel)}</span>
            </span>
            <button
              type="button"
              aria-label="Ziel verwerfen"
              onClick={() => {
                setZiel(null);
              }}
              className="flex min-h-12 min-w-12 items-center justify-center rounded-lg text-2xl font-bold active:bg-blue-100 dark:active:bg-blue-900"
            >
              ×
            </button>
          </div>
          <Knopf art="haupt" disabled={anzahl === 0} onClick={() => void alleVerschieben(ziel)}>
            {`${String(anzahl)} nach ${zielName(bestand, ziel)} verschieben`}
          </Knopf>
          {zielStammplatz && (
            <Knopf disabled={anzahl === 0 || !online} onClick={() => void alleStammplatz(ziel.id)}>
              {`${zielName(bestand, ziel)} als Stammplatz festlegen`}
            </Knopf>
          )}
          {zielStammplatz && !online && (
            <p className="text-xs">Stammplatz festlegen braucht Netz.</p>
          )}
        </section>
      )}

      <Scanner aufCode={(code) => void aufCode(code)} />

      {/*
        Die Aktionen direkt unter der Kamera (Rückmeldungen 4.2): Verschieben
        groß, Status und „Wieder da“ klein daneben. Den Stammplatz setzt der
        Haken beim Verschieben.
      */}
      {anzahl > 0 && (
        <section aria-label="Aktionen" className="flex flex-col gap-2">
          <Knopf
            art="haupt"
            onClick={() => {
              setAktion("wohin");
            }}
          >
            Verschieben
          </Knopf>
          <div className="grid grid-cols-2 gap-2">
            <Knopf
              onClick={() => {
                setAktion(aktion === "status" ? "aus" : "status");
              }}
            >
              Status
            </Knopf>
            <Knopf
              onClick={() => {
                setAktion("wiederDa");
              }}
            >
              Wieder da
            </Knopf>
          </div>
          {aktion === "status" && (
            <div className="rounded-xl bg-white p-3 dark:bg-neutral-800">
              <StatusWahl
                aktuell=""
                anzahl={anzahl}
                aufWahl={(schluessel, bezeichnung) => void alleStatus(schluessel, bezeichnung)}
              />
            </div>
          )}
        </section>
      )}

      <section aria-label="Sammelliste" className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">
            {anzahl === 0 ? "Sammelliste" : `Sammelliste · ${String(anzahl)}`}
          </h2>
          {anzahl > 0 && (
            <Knopf
              art="still"
              onClick={() => {
                setListe(() => []);
                setMeldung(null);
              }}
            >
              Liste leeren
            </Knopf>
          )}
        </div>
        {anzahl === 0 || bestand === undefined ? (
          <Leer>Scannen, was mitsoll. Ein Lagerort-QR wählt das Ziel.</Leer>
        ) : (
          <ol className="flex flex-col gap-1">
            {liste.map((id) => {
              const artikel = bestand.artikel.get(id);
              const zustand = bestand.zustaende.get(id);
              if (artikel === undefined) return null;
              return (
                <li
                  key={id}
                  className="flex items-center justify-between gap-2 rounded-xl bg-white py-1 pr-1 pl-3 dark:bg-neutral-800"
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="font-semibold">{artikel.bezeichnung}</span>
                    <span className="flex flex-wrap items-center gap-x-3 text-sm text-neutral-600 dark:text-neutral-300">
                      <Nummer>{artikel.inventarnummer}</Nummer>
                      {zustand !== undefined && <span>{woWar(bestand, zustand)}</span>}
                      {zustand !== undefined && <StatusPunkt status={zustand.status} />}
                    </span>
                  </span>
                  <button
                    type="button"
                    aria-label={`${artikel.bezeichnung} ${artikel.inventarnummer} entfernen`}
                    onClick={() => {
                      setListe((bisher) => bisher.filter((x) => x !== id));
                    }}
                    className="flex min-h-12 min-w-12 shrink-0 items-center justify-center rounded-lg text-2xl font-bold text-neutral-600 active:bg-neutral-200 dark:text-neutral-300 dark:active:bg-neutral-700"
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      <OhneCode
        aufArtikel={(id) => {
          void bestandLaden().then((b) => {
            hinzufuegen(b, id);
          });
        }}
        aufNichtDabei={(eingabe) => void aufCode(eingabe)}
      />
    </div>
  );
}
